import {Component, Vue} from 'vue-property-decorator';
import {moduleApi} from '@/api/organization';
import {ModuleResource, ModuleTile, LayoutResult, normalizeResources, isPlaced, moveModule, resizeModule, validLayout, layoutPayload, layoutSignature} from '@/js/moduleLayout';

/** 仅声明画布用到的观察器能力，兼容旧版TypeScript DOM类型库。 */
interface CanvasSizeObserver {
    /** 监听画布容器的尺寸变化。 */
    observe(target: Element): void;
    /** 页面销毁时解除监听。 */
    disconnect(): void;
}

interface Gesture {
    id: number; mode: 'move' | 'resize' | 'place'; toTray: boolean; pointerId: number;
    startX: number; startY: number; grabX: number; grabY: number;
    source: ModuleTile; base: ModuleTile[]; started: boolean;
    left: number; top: number; targetX: number; targetY: number; targetW: number; targetH: number;
    result: LayoutResult;
}

interface DragPreview {
    source: ModuleResource; left: number; top: number; width: number; height: number;
    compact: boolean; settling: boolean; invalid: boolean; label: string;
}

@Component({
    name: 'Sys_SourceManage',
    /** 离开当前路由前确认未保存的布局，取消离开时保留草稿。 */
    beforeRouteLeave(this: ResourceLayout, to: unknown, from: unknown, next: (value?: false) => void) {
        this.confirmLeave().then(allow => { if (allow) next(); else next(false); });
    }
})
export default class ResourceLayout extends Vue {
    modules: ModuleResource[] = [];
    original: Partial<ModuleResource>[] = [];
    undoStack: ModuleResource[][] = [];
    selectedId: number | null = null;
    loading = false;
    saving = false;
    searchText = '';
    boardWidth = 1130;
    readonly gap = 12;
    readonly rowHeight = 144;
    gesture: Gesture | null = null;
    dragPreview: DragPreview | null = null;
    landingId: number | null = null;
    captureTarget: HTMLElement | null = null;
    scrollLocks: {element: HTMLElement; left: number; top: number}[] = [];
    animationToken = 0;
    animationFrame = 0;
    animationTimer = 0;
    ignoreClickUntil = 0;
    menu = {visible: false, left: 0, top: 0};
    observer: CanvasSizeObserver | null = null;
    editId = 0;
    setDialog = false;
    detailSaving = false;
    addform = this.emptyForm();
    rules = {
        name: [{required: true, message: '请输入资源名称', trigger: 'blur'}],
        orderIndex: [{required: true, message: '请输入排序', trigger: 'blur'}],
        des: [{required: true, message: '请输入资源描述', trigger: 'blur'}],
        iconBack: [{required: true, message: '请输入背景色', trigger: 'blur'}],
        iconUrl: [{required: true, message: '请输入图片名', trigger: 'blur'}],
        layoutX: [{type: 'integer', min: 0, max: 8, message: '起始列须为0～8的整数', trigger: 'change'}],
        layoutY: [{type: 'integer', min: 0, max: 2, message: '起始行须为0～2的整数', trigger: 'change'}],
        layoutW: [{required: true, type: 'integer', min: 1, max: 9, message: '请输入1～9的整数', trigger: 'change'}],
        layoutH: [{required: true, type: 'integer', min: 1, max: 3, message: '请输入1～3的整数', trigger: 'change'}],
        iconSize: [{required: true, type: 'integer', min: 24, max: 96, message: '请输入24～96的整数', trigger: 'change'}],
    };

    /** 获取当前选中的资源，供工具栏、菜单和位置提示共同使用。 */
    get selected() { return this.modules.find(item => item.id === this.selectedId); }
    /** 对比服务端快照，移入未放置区也需要保存。 */
    get hasChanges() { return layoutSignature(this.modules) !== layoutSignature(this.original); }
    /** 在请求或拖动过程中禁止其它修改操作。 */
    get busy() { return this.loading || this.saving || this.detailSaving || !!this.gesture || !!this.dragPreview; }
    /** 根据画布宽度计算九列等宽栅格。 */
    get cellWidth() { return (this.boardWidth - this.gap * 8) / 9; }
    /** 主画布仅包含已放置的资源，未放置资源单独保留。 */
    get placedModules() { return this.modules.filter(isPlaced); }
    /** 起始列和起始行为空的资源不参与九列三行布局。 */
    get unplacedModules() { return this.modules.filter(item => !isPlaced(item)); }
    /** 有效拖动时预览整个交换结果，无效时保持所有按钮原位。 */
    get displayModules() { return this.gesture && this.gesture.started && this.gesture.result.valid ? this.gesture.result.items : this.placedModules; }
    /** 严格复用参考HTML的1130px宽、三行144px和12px间距。 */
    get boardStyle() {
        return {height: '456px', backgroundSize: (this.cellWidth + this.gap) + 'px 156px'};
    }
    /** 显示吸附落点及其有效状态。 */
    get dropStyle() {
        if (!this.gesture) return {};
        return this.rectStyle(this.gesture.targetX, this.gesture.targetY, this.gesture.targetW, this.gesture.targetH);
    }
    /** 悬浮副本使用视口坐标，跨区时不会被画布滚动容器裁剪。 */
    get ghostStyle() {
        const p = this.dragPreview;
        return p ? {transform: 'translate3d(' + p.left + 'px,' + p.top + 'px,0)', width: p.width + 'px', height: p.height + 'px'} : {};
    }
    /** 首次加载全部资源，画布不分页也不通过搜索重排。 */
    created() { this.loadModules(); }
    /** 安装指针与离页保护，观察画布宽度变化。 */
    mounted() {
        window.addEventListener('pointermove', this.onPointerMove, {passive: false});
        window.addEventListener('pointerup', this.onPointerUp);
        window.addEventListener('pointercancel', this.cancelGesture);
        window.addEventListener('blur', this.cancelGesture);
        window.addEventListener('keydown', this.onGlobalKey);
        window.addEventListener('beforeunload', this.onBeforeUnload);
        document.addEventListener('pointerdown', this.onOutsidePointer);
        window.addEventListener('resize', this.measureBoard);
        window.addEventListener('wheel', this.blockDragScroll, {passive: false, capture: true});
        window.addEventListener('touchmove', this.blockDragScroll, {passive: false, capture: true});
        document.addEventListener('scroll', this.restoreDragScroll, true);
        document.addEventListener('click', this.suppressDragClick, true);
        const layer = this.$refs.dragLayer as HTMLElement;
        if (layer) document.body.appendChild(layer);
        this.$nextTick(() => {
            this.measureBoard();
            const Observer = (window as Window & {
                ResizeObserver?: new (callback: () => void) => CanvasSizeObserver;
            }).ResizeObserver;
            if (Observer && this.$refs.viewport) {
                const observer = new Observer(() => this.measureBoard());
                observer.observe(this.$refs.viewport as HTMLElement);
                this.observer = observer;
            }
        });
    }
    /** 销毁时移除监听和尺寸观察，避免重复响应拖动。 */
    beforeDestroy() {
        window.removeEventListener('pointermove', this.onPointerMove);
        window.removeEventListener('pointerup', this.onPointerUp);
        window.removeEventListener('pointercancel', this.cancelGesture);
        window.removeEventListener('blur', this.cancelGesture);
        window.removeEventListener('keydown', this.onGlobalKey);
        window.removeEventListener('beforeunload', this.onBeforeUnload);
        document.removeEventListener('pointerdown', this.onOutsidePointer);
        window.removeEventListener('resize', this.measureBoard);
        window.removeEventListener('wheel', this.blockDragScroll, true);
        window.removeEventListener('touchmove', this.blockDragScroll, true);
        document.removeEventListener('scroll', this.restoreDragScroll, true);
        document.removeEventListener('click', this.suppressDragClick, true);
        const layer = this.$refs.dragLayer as HTMLElement;
        if (layer && layer.parentNode) layer.parentNode.removeChild(layer);
        if (this.observer) this.observer.disconnect();
        this.cancelGesture();
    }
    /** 缓存路由隐藏时终止当前手势，不保存中间状态。 */
    deactivated() { this.cancelGesture(); this.closeMenu(); }
    /** 缓存页面重新显示时重新测量画布，不使用隐藏状态的宽度。 */
    activated() { this.$nextTick(this.measureBoard); }

    /** 获取资源并保留未经画布规范化的原始值，用于服务端并发比对。 */
    async loadModules() {
        this.loading = true;
        try {
            const response = await moduleApi.list();
            if (response.data.result !== 0) throw new Error(response.data.message || '资源加载失败');
            this.acceptModules(response.data.data || []);
        } catch (error) { this.$message.error('资源加载失败，请刷新页面后重试'); }
        finally { this.loading = false; }
    }
    /** 接收服务端确认的配置，建立新的撤销及保存基线。 */
    acceptModules(rows: Partial<ModuleResource>[]) {
        const normalized = normalizeResources(rows);
        this.original = rows.map(item => ({...item}));
        this.modules = normalized;
        this.undoStack = [];
        if (!this.modules.some(item => item.id === this.selectedId)) this.selectedId = null;
        this.$nextTick(this.measureBoard);
    }
    /** 读取真实画布宽度，小屏横向滚动保持九列布局不变。 */
    measureBoard() {
        const board = this.$refs.board as HTMLElement;
        if (board && board.clientWidth > 0) this.boardWidth = board.clientWidth;
    }
    /** 将栅格矩形转换为可动画过渡的绝对位置。 */
    rectStyle(x: number, y: number, w: number, h: number) {
        return {transform: 'translate3d(' + x * (this.cellWidth + this.gap) + 'px,' + y * (this.rowHeight + this.gap) + 'px,0)',
            width: w * (this.cellWidth + this.gap) - this.gap + 'px', height: h * (this.rowHeight + this.gap) - this.gap + 'px'};
    }
    /** 定位和尺寸变化通过CSS补间，避免直接跳动。 */
    tileStyle(item: ModuleTile) { return this.rectStyle(item.layoutX, item.layoutY, item.layoutW, item.layoutH); }
    /** 标记选中、换位预览和搜索结果，不隐藏其它资源。 */
    tileClasses(item: ModuleTile) {
        return {'is-selected': item.id === this.selectedId,
            'is-source': this.gesture && this.gesture.started && item.id === this.gesture.id,
            'is-landing': this.landingId === item.id,
            'is-exchanged': this.gesture && this.gesture.result.exchanged.includes(item.id),
            'is-dimmed': this.searchText.trim() && !item.name.includes(this.searchText.trim())};
    }
    /** 复用项目原图标文件，缺失时使用已有默认图标。 */
    iconUrl(file: string) {
        try { return require('../../../static/image/indexPage/' + file); }
        catch (error) { return require('../../../static/image/indexPage/default.png'); }
    }
    /** 使用配置中的图标尺寸，限制在图标容器范围内。 */
    iconStyle(item: ModuleResource) { return {width: item.iconSize + 'px', height: item.iconSize + 'px'}; }
    /** 防止图标加载失败后反复触发错误。 */
    imageError(event: Event) {
        const image = event.target as HTMLImageElement;
        if (!image.dataset.fallback) { image.dataset.fallback = 'true'; image.src = this.iconUrl('default.png'); }
    }
    /** 开始手势并记录原始布局，超过移动阈值前仅选中按钮。 */
    startGesture(event: PointerEvent, item: ModuleResource, mode: 'move' | 'resize' | 'place') {
        if (event.button !== 0 || this.busy) return;
        event.preventDefault();
        event.stopPropagation();
        this.closeMenu();
        this.selectedId = item.id;
        const rect = (this.$refs.board as HTMLElement).getBoundingClientRect();
        const x = item.layoutX == null ? 0 : item.layoutX, y = item.layoutY == null ? 0 : item.layoutY;
        const left = x * (this.cellWidth + this.gap), top = y * (this.rowHeight + this.gap);
        const target = event.currentTarget as HTMLElement;
        const sourceElement = mode === 'resize' ? target.closest('.module-tile') as HTMLElement : target;
        const sourceRect = sourceElement.getBoundingClientRect();
        this.gesture = {id: item.id, mode, toTray: false, pointerId: event.pointerId, startX: event.clientX, startY: event.clientY,
            grabX: event.clientX - sourceRect.left, grabY: event.clientY - sourceRect.top,
            source: {...item, layoutX: x, layoutY: y}, base: this.placedModules.map(tile => ({...tile})), started: false, left, top,
            targetX: x, targetY: y, targetW: item.layoutW, targetH: item.layoutH,
            result: {valid: true, items: this.placedModules, reason: '', exchanged: []}};
        this.captureTarget = target;
        this.lockDragScroll(target);
        if (target.setPointerCapture) target.setPointerCapture(event.pointerId);
    }
    /** 跟随指针计算整套换位预览，固定画布和页面位置，不进行边缘滚动。 */
    onPointerMove(event: PointerEvent) {
        const g = this.gesture;
        if (!g || g.pointerId !== event.pointerId) return;
        if (!g.started && Math.hypot(event.clientX - g.startX, event.clientY - g.startY) < 5) return;
        event.preventDefault();
        g.started = true;
        const viewport = this.$refs.viewport as HTMLElement, bounds = viewport.getBoundingClientRect();
        const rect = (this.$refs.board as HTMLElement).getBoundingClientRect();
        const sourceWidth = g.source.layoutW * (this.cellWidth + this.gap) - this.gap;
        const sourceHeight = g.source.layoutH * (this.rowHeight + this.gap) - this.gap;
        const left = event.clientX - rect.left - Math.min(g.grabX, sourceWidth - 12);
        const top = event.clientY - rect.top - Math.min(g.grabY, sourceHeight - 12);
        if (g.mode !== 'resize') {
            g.left = left; g.top = top;
            g.targetX = Math.round(left / (this.cellWidth + this.gap));
            g.targetY = Math.round(top / (this.rowHeight + this.gap));
            if (g.mode === 'place') {
                const target = {...g.source, layoutX: g.targetX, layoutY: g.targetY};
                const items = [...g.base, target];
                g.result = {valid: validLayout(items), items, reason: '请将资源放入空位，或先将已有按钮移至未放置区', exchanged: []};
            } else g.result = moveModule(g.base, g.id, g.targetX, g.targetY);
        } else {
            g.targetW = Math.max(1, g.source.layoutW + Math.round((left - g.left) / (this.cellWidth + this.gap)));
            g.targetH = Math.max(1, g.source.layoutH + Math.round((top - g.top) / (this.rowHeight + this.gap)));
            g.result = resizeModule(g.base, g.id, g.targetW, g.targetH);
        }
        const tray = this.$refs.unplacedTray as HTMLElement;
        const trayRect = tray ? tray.getBoundingClientRect() : null;
        g.toTray = g.mode !== 'resize' && !!trayRect && event.clientX >= trayRect.left && event.clientX <= trayRect.right
            && event.clientY >= trayRect.top && event.clientY <= trayRect.bottom;
        if (g.toTray) g.result = {valid: true, items: g.base, reason: '', exchanged: []};
        else if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) {
            g.result = {valid: false, items: g.base, reason: '请在画布内松开鼠标', exchanged: []};
        }
        const compact = g.toTray || (g.mode === 'place' && (event.clientY < bounds.top || event.clientY > bounds.bottom));
        const width = compact ? 196 : g.targetW * (this.cellWidth + this.gap) - this.gap;
        const height = compact ? 46 : g.targetH * (this.rowHeight + this.gap) - this.gap;
        this.dragPreview = {source: g.source, compact, settling: false, invalid: !g.result.valid, width, height,
            left: g.mode === 'resize' ? rect.left + g.left : event.clientX - Math.min(g.grabX, width - 12),
            top: g.mode === 'resize' ? rect.top + g.top : event.clientY - Math.min(g.grabY, height - 12),
            label: g.toTray ? '松开移至未放置区' : g.result.valid ? '松开放置' : '此处不可放置'};
    }
    /** 松开后提交有效预览，浮层沿路径落入新区域；无效操作动画回到原位。 */
    async onPointerUp(event: PointerEvent) {
        const g = this.gesture;
        if (!g || g.pointerId !== event.pointerId) return;
        if (g.started) this.onPointerMove(event);
        if (!g.started || !this.dragPreview) { this.cancelGesture(); return; }
        const token = ++this.animationToken;
        this.ignoreClickUntil = Date.now() + 160;
        this.landingId = g.id;
        if (g.result.valid) {
            if (g.toTray) this.commitResources(this.modules.map(item => item.id === g.id ? {...item, layoutX: null, layoutY: null} : item));
            else this.commitLayout(g.result.items);
        } else this.$message.warning(g.result.reason);
        this.gesture = null;
        this.releasePointer(g.pointerId);
        await this.$nextTick();
        if (token !== this.animationToken || !this.dragPreview) return;
        const item = this.modules.find(row => row.id === g.id);
        const element = this.$el.querySelector('[data-module-id="' + g.id + '"]') as HTMLElement;
        if (!item || !element) { this.cancelGesture(); return; }
        const boardRect = (this.$refs.board as HTMLElement).getBoundingClientRect();
        const rect = element.getBoundingClientRect();
        const placed = isPlaced(item);
        const tile = placed ? item as ModuleTile : null;
        const left = tile ? boardRect.left + tile.layoutX * (this.cellWidth + this.gap) : rect.left;
        const top = tile ? boardRect.top + tile.layoutY * (this.rowHeight + this.gap) : rect.top;
        const width = placed ? item.layoutW * (this.cellWidth + this.gap) - this.gap : rect.width;
        const height = placed ? item.layoutH * (this.rowHeight + this.gap) - this.gap : rect.height;
        this.animationFrame = requestAnimationFrame(() => {
            if (token !== this.animationToken || !this.dragPreview) return;
            this.dragPreview = {...this.dragPreview, left, top, width, height, compact: !placed, settling: true, invalid: false, label: ''};
            const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            this.animationTimer = window.setTimeout(() => {
                if (token === this.animationToken) { this.dragPreview = null; this.landingId = null; this.scrollLocks = []; }
            }, reducedMotion ? 0 : 300);
        });
    }
    /** 解除显式指针捕获，避免跨区后鼠标事件仍落在旧卡片上。 */
    releasePointer(pointerId: number) {
        const target = this.captureTarget;
        if (target && target.hasPointerCapture && target.hasPointerCapture(pointerId)) target.releasePointerCapture(pointerId);
        this.captureTarget = null;
    }
    /** 取消手势并清理浮层、动画和滚动锁，不留下透明遮罩。 */
    cancelGesture() {
        if (this.gesture) this.releasePointer(this.gesture.pointerId);
        this.animationToken++;
        if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
        if (this.animationTimer) window.clearTimeout(this.animationTimer);
        this.gesture = null;
        this.dragPreview = null;
        this.landingId = null;
        this.scrollLocks = [];
    }
    /** 记录画布及祖先滚动位置，整个拖动和落位过程保持页面静止。 */
    lockDragScroll(source: HTMLElement) {
        const elements = new Set<HTMLElement>();
        let current: HTMLElement | null = source;
        while (current) { elements.add(current); current = current.parentElement; }
        elements.add(this.$refs.viewport as HTMLElement);
        if (document.scrollingElement) elements.add(document.scrollingElement as HTMLElement);
        this.scrollLocks = Array.from(elements).map(element => ({element, left: element.scrollLeft, top: element.scrollTop}));
    }
    /** 拖动时阻止滚轮及触摸滚动，普通浏览时仍可正常滚动页面。 */
    blockDragScroll(event: Event) { if (this.gesture || this.dragPreview) event.preventDefault(); }
    /** 还原浏览器或祖先容器触发的滚动，防止拖动时坐标基准变化。 */
    restoreDragScroll() {
        if (!this.gesture && !this.dragPreview) return;
        this.scrollLocks.forEach(lock => {
            if (lock.element.scrollLeft !== lock.left) lock.element.scrollLeft = lock.left;
            if (lock.element.scrollTop !== lock.top) lock.element.scrollTop = lock.top;
        });
    }
    /** 拖动结束后吞掉浏览器合成的点击，避免命中下面的按钮或画布。 */
    suppressDragClick(event: MouseEvent) {
        if (Date.now() < this.ignoreClickUntil) { event.preventDefault(); event.stopImmediatePropagation(); }
    }
    /** 保存一次可撤销操作，避免无效点击产生历史记录。 */
    commitLayout(items: ModuleTile[]) {
        this.commitResources([...items, ...this.unplacedModules.filter(item => !items.some(tile => tile.id === item.id))]);
    }
    /** 连同未放置区一起记录完整布局，保证撤销和批量保存不丢资源。 */
    commitResources(items: ModuleResource[]) {
        if (layoutSignature(items) === layoutSignature(this.modules)) return;
        this.undoStack = [...this.undoStack.slice(-29), this.modules.map(item => ({...item}))];
        this.modules = items.map(item => ({...item}));
    }
    /** 撤销最近一次移动或缩放。 */
    undo() { if (this.busy) return; const previous = this.undoStack.pop(); if (previous) this.modules = previous; }
    /** 原子保存整套布局及原始快照，失败时保留当前草稿。 */
    async saveLayout(): Promise<boolean> {
        if (this.busy) return false;
        if (!this.hasChanges) return true;
        if (!validLayout(this.placedModules)) { this.$message.error('布局存在重叠或越界，请调整后再保存'); return false; }
        this.saving = true;
        try {
            const response = await moduleApi.saveLayout({items: layoutPayload(this.modules), original: layoutPayload(this.original)});
            if (response.data.result !== 0) { this.$message.error(response.data.message || '保存失败，当前布局已保留'); return false; }
            this.acceptModules(response.data.data);
            this.$message.success('布局已保存');
            return true;
        } catch (error) { this.$message.error('布局保存失败，当前调整已保留，请检查后端服务后重试'); return false; }
        finally { this.saving = false; }
    }
    /** 执行其它资料操作前确认保存布局，取消时保留草稿。 */
    async prepareAction(): Promise<boolean> {
        if (!this.hasChanges) return true;
        try { await this.$confirm('当前布局有未保存的调整，是否先保存再继续？', '保存布局', {confirmButtonText: '保存并继续', cancelButtonText: '返回调整', type: 'warning'}); }
        catch (error) { return false; }
        return this.saveLayout();
    }
    /** 复用新增、编辑、权限设置和单个删除操作。 */
    async runAction(action: string) {
        if (this.busy) return;
        this.closeMenu();
        const selected = this.selected;
        if (action !== 'add' && !selected) return;
        if (action !== 'unplace' && action !== 'place' && !await this.prepareAction()) return;
        if (action === 'unplace' && selected) { this.unplaceResource(selected); return; }
        if (action === 'place' && selected) { this.placeResource(selected); return; }
        if (action === 'permission' && selected) { this.$router.push('/sys/resource/' + selected.id); return; }
        if (action === 'delete' && selected) { await this.deleteResource(selected); return; }
        this.editId = action === 'add' ? 0 : (selected as ModuleTile).id;
        this.addform = this.emptyForm();
        if (this.editId) {
            this.loading = true;
            try {
                const response = await moduleApi.getOne({id: this.editId});
                if (response.data.result !== 0 || !response.data.data) { this.$message.error('资源详情加载失败'); return; }
                const row = response.data.data;
                this.addform = {...this.addform, ...row, layoutX: row.layoutX == null ? undefined : row.layoutX,
                    layoutY: row.layoutY == null ? undefined : row.layoutY};
            } catch (error) { this.$message.error('资源详情加载失败'); return; }
            finally { this.loading = false; }
        }
        this.setDialog = true;
        this.$nextTick(() => { const form = this.$refs.resourceForm as HTMLFormElement; if (form) form.clearValidate(); });
    }
    /** 构造新增默认值，防止上一条记录的ID或布局残留。 */
    emptyForm(): any {
        return {name: '', orderIndex: Math.max(0, ...((this.modules || []).map(item => item.orderIndex || 0))) + 1,
            des: '', iconBack: '#229FDA', iconUrl: 'default.png', pageType: 0,
            layoutX: undefined, layoutY: undefined, layoutW: 1, layoutH: 1, iconSize: 48};
    }
    /** 校验单项资料和位置，拒绝弹框保存产生重叠。 */
    saveResource() {
        const form = this.$refs.resourceForm as HTMLFormElement;
        form.validate(async (valid: boolean) => {
            if (!valid || this.detailSaving) return;
            const hasX = this.addform.layoutX != null, hasY = this.addform.layoutY != null;
            if (hasX !== hasY) { this.$message.error('起始列和起始行须同时填写或留空'); return; }
            const item = {...this.addform, id: this.editId || -1} as ModuleTile;
            const others = this.placedModules.filter(tile => tile.id !== this.editId);
            if (hasX && !validLayout([...others, item])) { this.$message.error('位置越界或与其它按钮重叠，请调整位置或留空移至未放置区'); return; }
            const params = {...this.addform, layoutX: hasX ? item.layoutX : null, layoutY: hasY ? item.layoutY : null};
            this.detailSaving = true;
            try {
                const response = this.editId ? await moduleApi.updateById({...params, id: this.editId}) : await moduleApi.add(params);
                if (response.data.result !== 0) { this.$message.error(response.data.message || '资源保存失败'); return; }
                this.setDialog = false;
                await this.loadModules();
                this.$message.success('资源已保存');
            } catch (error) { this.$message.error('资源保存失败，请重试'); }
            finally { this.detailSaving = false; }
        });
    }
    /** 确认后删除单个资源，沿用原有删除接口。 */
    async deleteResource(item: ModuleResource) {
        try { await this.$confirm('确定删除“' + item.name + '”？', '删除资源', {type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消'}); }
        catch (error) { return; }
        this.loading = true;
        try {
            const response = await moduleApi.delete({ids: String(item.id)});
            if (response.data.result !== 0) { this.$message.error(response.data.message || '删除失败'); return; }
            await this.loadModules(); this.$message.success('资源已删除');
        } catch (error) { this.$message.error('删除失败，请重试'); }
        finally { this.loading = false; }
    }
    /** 将资源移至未放置区，保留记录、资料及权限，仅清空位置。 */
    unplaceResource(item: ModuleResource) {
        this.commitResources(this.modules.map(row => row.id === item.id ? {...row, layoutX: null, layoutY: null} : row));
    }
    /** 从未放置区选择首个可容纳位置，不扩展第四行。 */
    placeResource(item: ModuleResource) {
        for (let y = 0; y < 3; y++) {
            for (let x = 0; x < 9; x++) {
                const candidate = {...item, layoutX: x, layoutY: y};
                if (validLayout([...this.placedModules, candidate])) {
                    this.commitResources(this.modules.map(row => row.id === item.id ? candidate : row));
                    return;
                }
            }
        }
        this.$message.warning('九列三行内没有足够空位，请先移出其它资源');
    }
    /** 在光标或菜单按钮处打开菜单，并避让视口边缘。 */
    showMenu(event: MouseEvent, item: ModuleResource) {
        if (this.busy) return;
        this.selectedId = item.id;
        this.menu = {visible: true, left: Math.max(8, Math.min(event.clientX, window.innerWidth - 200)),
            top: Math.max(8, Math.min(event.clientY, window.innerHeight - 248))};
        this.$nextTick(() => { const menu = this.$refs.contextMenu as HTMLElement; if (menu) { const first = menu.querySelector('button'); if (first) first.focus(); } });
    }
    /** 关闭菜单并保持资源选中状态。 */
    closeMenu() { this.menu.visible = false; }
    /** 点击菜单外区域时收起菜单。 */
    onOutsidePointer(event: PointerEvent) {
        const menu = this.$refs.contextMenu as HTMLElement;
        if (menu && !menu.contains(event.target as Node)) this.closeMenu();
    }
    /** 搜索只定位和高亮目标，不破坏原有坐标。 */
    locateResource() {
        const item = this.modules.find(tile => tile.name.includes(this.searchText.trim()));
        if (!item) { this.$message.info('没有找到对应资源'); return; }
        this.selectedId = item.id;
        this.$nextTick(() => {
            const tile = this.$el.querySelector('[data-module-id="' + item.id + '"]') as HTMLElement;
            if (tile) { tile.scrollIntoView({block: 'nearest', inline: 'nearest', behavior: 'smooth'}); tile.focus({preventScroll: true}); }
        });
    }
    /** 方向键移动，Shift或缩放手柄上的方向键调整尺寸。 */
    onTileKey(event: KeyboardEvent, item: ModuleTile, resize = false) {
        if (this.busy) return;
        this.selectedId = item.id;
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); return; }
        const directions: {[key: string]: number[]} = {ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1]};
        const delta = directions[event.key];
        if (!delta) return;
        event.preventDefault();
        const result = resize || event.shiftKey ? resizeModule(this.placedModules, item.id, item.layoutW + delta[0], item.layoutH + delta[1])
            : moveModule(this.placedModules, item.id, item.layoutX + delta[0], item.layoutY + delta[1]);
        if (result.valid) this.commitLayout(result.items); else this.$message.warning(result.reason);
    }
    /** Escape取消手势及菜单，恢复调整前的位置。 */
    onGlobalKey(event: KeyboardEvent) { if (event.key === 'Escape') { this.cancelGesture(); this.closeMenu(); } }
    /** 浏览器刷新或关闭前提醒未保存的调整。 */
    onBeforeUnload(event: BeforeUnloadEvent) { if (this.hasChanges) { event.preventDefault(); event.returnValue = ''; } }
    /** 路由切换时可明确放弃草稿，保存中的请求则阻止离开。 */
    async confirmLeave(): Promise<boolean> {
        this.cancelGesture(); this.closeMenu();
        if (this.saving || this.detailSaving) return false;
        if (!this.hasChanges) return true;
        try {
            await this.$confirm('当前布局尚未保存，离开将放弃本次调整。', '离开资源管理', {confirmButtonText: '放弃并离开', cancelButtonText: '继续调整', type: 'warning'});
            this.acceptModules(this.original);
            return true;
        }
        catch (error) { return false; }
    }
}
